import { useLoggedInMember, useLoggedInOrganization } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import { FEATURE_ACCESS_CONFIG, FeatureName } from "@/shared/config/feature-access";
import { ROUTES } from "@/shared/constants/routes";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ReactNode, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

interface ProtectedRouteProps {
  children: ReactNode;
  featureName: FeatureName;
  redirectTo?: string;
}

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require feature flag or permission-based access control.
 * Automatically redirects users who don't have access to a specified route.
 *
 * @example
 * <ProtectedRoute featureName="tpraDashboard">
 *   <TPRADashboard />
 * </ProtectedRoute>
 *
 * @example
 * <ProtectedRoute featureName="tpraDashboard" redirectTo="/agent">
 *   <TPRADashboard />
 * </ProtectedRoute>
 */
export const ProtectedRoute = ({ children, featureName, redirectTo }: ProtectedRouteProps) => {
  const flags = useFlags();
  const loggedInMember = useLoggedInMember();
  const loggedInOrganization = useLoggedInOrganization();
  const navigate = useNavigate();

  const config = FEATURE_ACCESS_CONFIG[featureName];
  const defaultRedirect = config?.redirectTo || ROUTES.AGENT;
  const finalRedirectPath = redirectTo || defaultRedirect;

  // Get user permissions
  const userPermissions = loggedInMember ? getZaniaTeamMemberRoles("", loggedInMember) : {};

  // Check if user has access
  const hasAccess = (() => {
    if (!config) return true;

    // Check feature flag if specified
    if (config.featureFlag) {
      const flagValue = flags[config.featureFlag];

      // Handle org-specific feature flags (object format: { "orgId": true })
      if (typeof flagValue === "object" && flagValue !== null) {
        const orgId = loggedInOrganization?.organization_id;
        if (!orgId) return false;

        const hasOrgAccess = flagValue[orgId] === true;
        if (!hasOrgAccess) return false;
      }
      // Handle boolean feature flags
      else if (typeof flagValue === "boolean") {
        if (!flagValue) return false;
      }
      // If flag exists but is not boolean or object, deny access
      else if (flagValue !== undefined && flagValue !== true) {
        return false;
      }
    }

    // Check permission if specified
    if (config.permission) {
      const hasPermission = userPermissions[config.permission as keyof typeof userPermissions];
      if (!hasPermission) return false;
    }

    return true;
  })();

  // Redirect if no access
  useEffect(() => {
    if (!hasAccess) {
      navigate(finalRedirectPath, { replace: true });
    }
  }, [hasAccess, finalRedirectPath, navigate]);

  // Don't render children if no access
  if (!hasAccess) {
    return <Navigate to={finalRedirectPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
